import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import './LeadsPage.css'

const STATUSES = ['new', 'contacted', 'working', 'qualified', 'unqualified', 'lost', 'converted']
const ACTIVITY_TYPES = [
  ['call', 'Call'],
  ['email', 'Email'],
  ['meeting', 'Meeting'],
  ['note', 'Note'],
  ['follow_up', 'Follow-up'],
  ['task', 'Task'],
  ['other', 'Other'],
]
const CONTACT_ACTIVITY_TYPES = ['call', 'email', 'meeting']

const emptyLead = {
  company_name: '', contact_name: '', email: '', phone: '', source: '', need: '', status: 'new',
  next_follow_up_at: '', expected_close_date: '', next_step: '', notes: '',
}

function localDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function nowLocalDateTime() {
  return localDateTime(new Date().toISOString())
}

function displayDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

function normalizePayload(form) {
  return {
    company_name: form.company_name.trim() || null,
    contact_name: form.contact_name.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    source: form.source.trim() || null,
    need: form.need.trim() || null,
    status: form.status || 'new',
    next_follow_up_at: form.next_follow_up_at ? new Date(form.next_follow_up_at).toISOString() : null,
    expected_close_date: form.expected_close_date || null,
    next_step: form.next_step.trim() || null,
    notes: form.notes.trim() || null,
  }
}

function activityLabel(type) {
  return ACTIVITY_TYPES.find(([value]) => value === type)?.[1] || type || 'Activity'
}

export default function LeadsPage({ tenantId, userId }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyLead)
  const [selectedLead, setSelectedLead] = useState(null)
  const [activities, setActivities] = useState([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityBusy, setActivityBusy] = useState(false)
  const [activityForm, setActivityForm] = useState({ activity_type: 'call', subject: '', details: '', completed_at: nowLocalDateTime() })

  async function load() {
    setLoading(true)
    const { data, error: queryError } = await supabase
      .from('leads')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(250)
    const nextRows = data || []
    setRows(nextRows)
    setSelectedLead((current) => current ? (nextRows.find((row) => row.id === current.id) || null) : null)
    setError(queryError?.message || '')
    setLoading(false)
  }

  useEffect(() => { load() }, [tenantId])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((row) => [row.company_name, row.contact_name, row.email, row.phone, row.source, row.status, row.next_step]
      .some((value) => String(value || '').toLowerCase().includes(term)))
  }, [rows, search])

  async function loadActivities(leadId) {
    if (!leadId) return
    setActivitiesLoading(true)
    const { data, error: activityError } = await supabase
      .from('activities')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false })
      .limit(100)
    setActivities(data || [])
    if (activityError) setError(activityError.message)
    setActivitiesLoading(false)
  }

  function openLead(row) {
    setSelectedLead(row)
    setError('')
    setMessage('')
    setActivityOpen(false)
    loadActivities(row.id)
  }

  function startCreate() {
    setEditingId(null)
    setForm(emptyLead)
    setError('')
    setMessage('')
    setEditorOpen(true)
  }

  function startEdit(row) {
    setEditingId(row.id)
    setForm({
      company_name: row.company_name || '',
      contact_name: row.contact_name || '',
      email: row.email || '',
      phone: row.phone || '',
      source: row.source || '',
      need: row.need || '',
      status: row.status || 'new',
      next_follow_up_at: localDateTime(row.next_follow_up_at),
      expected_close_date: row.expected_close_date || '',
      next_step: row.next_step || '',
      notes: row.notes || '',
    })
    setError('')
    setMessage('')
    setEditorOpen(true)
  }

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function save(event) {
    event.preventDefault()
    if (!form.company_name.trim() && !form.contact_name.trim()) {
      setError('Enter a company name or contact name.')
      return
    }
    setBusy(true)
    setError('')
    setMessage('')
    const payload = normalizePayload(form)
    const result = editingId
      ? await supabase.from('leads').update(payload).eq('id', editingId).eq('tenant_id', tenantId)
      : await supabase.from('leads').insert({ ...payload, tenant_id: tenantId, assigned_user_id: userId })
    setBusy(false)
    if (result.error) {
      setError(result.error.message)
      return
    }
    setMessage(editingId ? 'Lead updated.' : 'Lead created.')
    setEditorOpen(false)
    setEditingId(null)
    setForm(emptyLead)
    await load()
  }

  async function qualify(row) {
    if (['qualified', 'converted'].includes(row.status)) return
    setBusy(true)
    setError('')
    setMessage('')
    const { data, error: rpcError } = await supabase.rpc('qualify_lead', { p_lead_id: row.id })
    setBusy(false)
    if (rpcError) {
      setError(rpcError.message)
      return
    }
    setMessage('Lead qualified. Account, contact, and opportunity are ready.')
    await load()
    if (data) window.location.hash = '/opportunities'
  }

  async function remove(row) {
    if (!window.confirm(`Delete ${row.company_name || row.contact_name || 'this lead'}?`)) return
    setBusy(true)
    setError('')
    const { error: deleteError } = await supabase.from('leads').delete().eq('id', row.id).eq('tenant_id', tenantId)
    setBusy(false)
    if (deleteError) return setError(deleteError.message)
    if (selectedLead?.id === row.id) setSelectedLead(null)
    setMessage('Lead deleted.')
    await load()
  }

  function startActivity() {
    setActivityForm({ activity_type: 'call', subject: '', details: '', completed_at: nowLocalDateTime() })
    setActivityOpen(true)
    setError('')
    setMessage('')
  }

  function changeActivity(field, value) {
    setActivityForm((current) => ({ ...current, [field]: value }))
  }

  async function saveActivity(event) {
    event.preventDefault()
    if (!selectedLead) return
    setActivityBusy(true)
    setError('')
    setMessage('')
    const completedAt = activityForm.completed_at ? new Date(activityForm.completed_at).toISOString() : new Date().toISOString()
    const defaultSubject = `${activityLabel(activityForm.activity_type)} — ${selectedLead.company_name || selectedLead.contact_name || 'Lead'}`
    const { error: activityError } = await supabase.from('activities').insert({
      tenant_id: tenantId,
      assigned_user_id: userId,
      lead_id: selectedLead.id,
      activity_type: activityForm.activity_type,
      subject: activityForm.subject.trim() || defaultSubject,
      details: activityForm.details.trim() || null,
      completed_at: completedAt,
    })
    if (activityError) {
      setActivityBusy(false)
      setError(activityError.message)
      return
    }

    if (CONTACT_ACTIVITY_TYPES.includes(activityForm.activity_type)) {
      const leadUpdate = { last_contact_at: completedAt }
      if (selectedLead.status === 'new') leadUpdate.status = 'contacted'
      const { error: leadUpdateError } = await supabase.from('leads').update(leadUpdate).eq('id', selectedLead.id).eq('tenant_id', tenantId)
      if (leadUpdateError) setError(`Activity logged, but the lead could not be updated: ${leadUpdateError.message}`)
    }

    setActivityBusy(false)
    setActivityOpen(false)
    setMessage(`${activityLabel(activityForm.activity_type)} logged.`)
    await Promise.all([loadActivities(selectedLead.id), load()])
  }

  function leadKeyHandler(event, row) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openLead(row)
    }
  }

  return (
    <div className="stack leads-page">
      <section className="hero compact leads-hero">
        <div><p className="eyebrow">CRM / INTERNAL SALES</p><h1>Leads</h1><p>Capture prospects, schedule follow-ups, and convert qualified leads into the sales pipeline.</p></div>
        <button className="hero-action" onClick={startCreate}>+ New Lead</button>
      </section>

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <section className="panel table-panel leads-panel">
        <div className="toolbar action-toolbar leads-toolbar">
          <input placeholder="Search leads…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div><span>{filtered.length} shown</span><button className="primary-action" onClick={startCreate}>New Lead</button></div>
        </div>
        {loading ? <div className="loading-inline">Loading leads…</div> : <>
          <div className="table-wrap lead-table-desktop"><table><thead><tr>
            <th>Company / Contact</th><th>Status</th><th>Source</th><th>Next Follow-Up</th><th>Next Step</th><th>Actions</th>
          </tr></thead><tbody>
            {filtered.length ? filtered.map((row) => <tr className="lead-row" key={row.id} tabIndex="0" onClick={() => openLead(row)} onKeyDown={(event) => leadKeyHandler(event, row)}>
              <td><strong>{row.company_name || row.contact_name || 'Unnamed lead'}</strong><small className="cell-sub">{row.company_name ? row.contact_name : row.email}{row.email && row.company_name ? ` · ${row.email}` : ''}</small></td>
              <td><span className="pill">{row.status}</span></td>
              <td>{row.source || '—'}</td>
              <td>{displayDate(row.next_follow_up_at)}</td>
              <td className="next-step-cell">{row.next_step || '—'}</td>
              <td><div className="row-actions" onClick={(event) => event.stopPropagation()}><button onClick={() => startEdit(row)}>Edit</button>{!['qualified', 'converted'].includes(row.status) && <button className="accent-action" disabled={busy} onClick={() => qualify(row)}>Qualify</button>}<button className="danger-action" disabled={busy} onClick={() => remove(row)}>Delete</button></div></td>
            </tr>) : <tr><td colSpan="6">No leads found.</td></tr>}
          </tbody></table></div>

          <div className="lead-mobile-list">
            {filtered.length ? filtered.map((row) => <button type="button" className="lead-mobile-card" key={row.id} onClick={() => openLead(row)}>
              <div className="lead-mobile-head"><div><strong>{row.company_name || row.contact_name || 'Unnamed lead'}</strong>{row.company_name && row.contact_name && <small>{row.contact_name}</small>}</div><span className="pill">{row.status}</span></div>
              {(row.email || row.phone) && <div className="lead-mobile-contact">{row.email || row.phone}</div>}
              <div className="lead-mobile-meta"><span><small>Next follow-up</small><strong>{displayDate(row.next_follow_up_at)}</strong></span><span><small>Next step</small><strong>{row.next_step || '—'}</strong></span></div>
              <span className="lead-open-label">Open lead ›</span>
            </button>) : <div className="lead-mobile-empty">No leads found.</div>}
          </div>
        </>}
      </section>

      {selectedLead && <div className="lead-detail-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedLead(null) }}>
        <aside className="lead-detail-panel">
          <div className="lead-detail-head">
            <div><p className="eyebrow">LEAD RECORD</p><h2>{selectedLead.company_name || selectedLead.contact_name || 'Unnamed lead'}</h2>{selectedLead.company_name && selectedLead.contact_name && <p>{selectedLead.contact_name}</p>}</div>
            <button className="lead-close" onClick={() => setSelectedLead(null)} aria-label="Close lead">×</button>
          </div>

          <div className="lead-detail-actions">
            <button className="primary-action" onClick={startActivity}>+ Log Activity</button>
            <button className="secondary-action" onClick={() => startEdit(selectedLead)}>Edit</button>
            {!['qualified', 'converted'].includes(selectedLead.status) && <button className="accent-action" disabled={busy} onClick={() => qualify(selectedLead)}>Qualify</button>}
            <button className="danger-action" disabled={busy} onClick={() => remove(selectedLead)}>Delete</button>
          </div>

          <div className="lead-detail-grid">
            <div><small>Status</small><span className="pill">{selectedLead.status}</span></div>
            <div><small>Source</small><strong>{selectedLead.source || '—'}</strong></div>
            <div><small>Email</small>{selectedLead.email ? <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a> : <strong>—</strong>}</div>
            <div><small>Phone</small>{selectedLead.phone ? <a href={`tel:${selectedLead.phone}`}>{selectedLead.phone}</a> : <strong>—</strong>}</div>
            <div><small>Last contact</small><strong>{displayDate(selectedLead.last_contact_at)}</strong></div>
            <div><small>Next follow-up</small><strong>{displayDate(selectedLead.next_follow_up_at)}</strong></div>
            <div className="wide"><small>Need / Opportunity</small><strong>{selectedLead.need || '—'}</strong></div>
            <div className="wide"><small>Next step</small><strong>{selectedLead.next_step || '—'}</strong></div>
            <div className="wide"><small>Notes</small><strong>{selectedLead.notes || '—'}</strong></div>
          </div>

          <section className="activity-section">
            <div className="activity-section-head"><div><p className="eyebrow">ACTIVITY</p><h3>Activity log</h3></div><button className="secondary-action" onClick={startActivity}>Log Activity</button></div>
            {activitiesLoading ? <div className="activity-empty">Loading activity…</div> : activities.length ? <div className="activity-list">
              {activities.map((activity) => <article className="activity-item" key={activity.id}>
                <div className={`activity-icon activity-${activity.activity_type}`}>{activityLabel(activity.activity_type).slice(0, 1)}</div>
                <div><div className="activity-title"><strong>{activity.subject || activityLabel(activity.activity_type)}</strong><span>{displayDate(activity.completed_at || activity.created_at)}</span></div><small>{activityLabel(activity.activity_type)}</small>{activity.details && <p>{activity.details}</p>}</div>
              </article>)}
            </div> : <div className="activity-empty">No activity logged yet. Record a call, email, meeting, note, or other touchpoint.</div>}
          </section>
        </aside>
      </div>}

      {editorOpen && <div className="editor-backdrop lead-editor-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setEditorOpen(false) }}>
        <section className="editor-panel lead-editor-panel">
          <div className="editor-head"><div><p className="eyebrow">{editingId ? 'EDIT LEAD' : 'NEW LEAD'}</p><h2>{editingId ? 'Update prospect' : 'Add prospect'}</h2></div><button onClick={() => setEditorOpen(false)}>×</button></div>
          <form className="business-form" onSubmit={save}>
            <div className="form-grid">
              <label>Company<input value={form.company_name} onChange={(e) => change('company_name', e.target.value)} placeholder="Company or organization" /></label>
              <label>Contact Name<input value={form.contact_name} onChange={(e) => change('contact_name', e.target.value)} placeholder="Primary contact" /></label>
              <label>Email<input type="email" value={form.email} onChange={(e) => change('email', e.target.value)} /></label>
              <label>Phone<input value={form.phone} onChange={(e) => change('phone', e.target.value)} /></label>
              <label>Source<input value={form.source} onChange={(e) => change('source', e.target.value)} placeholder="Referral, website, outbound…" /></label>
              <label>Status<select value={form.status} onChange={(e) => change('status', e.target.value)}>{STATUSES.map((status) => <option key={status} value={status}>{status.replaceAll('_', ' ')}</option>)}</select></label>
              <label>Next Follow-Up<input type="datetime-local" value={form.next_follow_up_at} onChange={(e) => change('next_follow_up_at', e.target.value)} /></label>
              <label>Expected Close<input type="date" value={form.expected_close_date} onChange={(e) => change('expected_close_date', e.target.value)} /></label>
              <label className="full-field">Need / Opportunity<textarea rows="3" value={form.need} onChange={(e) => change('need', e.target.value)} placeholder="What does this prospect need?" /></label>
              <label className="full-field">Next Step<input value={form.next_step} onChange={(e) => change('next_step', e.target.value)} placeholder="Call, demo, quote, site visit…" /></label>
              <label className="full-field">Notes<textarea rows="4" value={form.notes} onChange={(e) => change('notes', e.target.value)} /></label>
            </div>
            <div className="form-actions"><button type="button" className="secondary-action" onClick={() => setEditorOpen(false)}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? 'Saving…' : editingId ? 'Save Changes' : 'Create Lead'}</button></div>
          </form>
        </section>
      </div>}

      {activityOpen && selectedLead && <div className="editor-backdrop activity-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setActivityOpen(false) }}>
        <section className="editor-panel activity-editor">
          <div className="editor-head"><div><p className="eyebrow">LOG ACTIVITY</p><h2>{selectedLead.company_name || selectedLead.contact_name || 'Lead'}</h2></div><button onClick={() => setActivityOpen(false)}>×</button></div>
          <form className="business-form" onSubmit={saveActivity}>
            <div className="form-grid activity-form-grid">
              <label>Activity Type<select value={activityForm.activity_type} onChange={(e) => changeActivity('activity_type', e.target.value)}>{ACTIVITY_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>Date / Time<input type="datetime-local" value={activityForm.completed_at} onChange={(e) => changeActivity('completed_at', e.target.value)} /></label>
              <label className="full-field">Subject<input value={activityForm.subject} onChange={(e) => changeActivity('subject', e.target.value)} placeholder={`${activityLabel(activityForm.activity_type)} with ${selectedLead.company_name || selectedLead.contact_name || 'lead'}`} /></label>
              <label className="full-field">Details<textarea rows="5" value={activityForm.details} onChange={(e) => changeActivity('details', e.target.value)} placeholder="What happened? Add notes, outcome, and any follow-up needed." /></label>
            </div>
            <div className="form-actions"><button type="button" className="secondary-action" onClick={() => setActivityOpen(false)}>Cancel</button><button className="primary-action" disabled={activityBusy}>{activityBusy ? 'Logging…' : 'Log Activity'}</button></div>
          </form>
        </section>
      </div>}
    </div>
  )
}
